// Every event listener in the game: the front-end's navigation, and the controls
// once a match is under way. Kept apart from flow.js so the rules stay readable
// without DOM plumbing in the middle.

import { state } from './state.js';
import { clamp } from './utils.js';
import { W, H } from './canvas.js';
import { grabCrate } from './crates.js';
import {
  angleSlider,
  applyCharacterClick,
  awardClose,
  awardPanel,
  awardsBtn,
  charList,
  charPanel,
  charSave,
  charsBtn,
  closePanels,
  fireBtn,
  fxVol,
  gameCanvas,
  humanSide,
  againstCpuEl,
  againstRandomEl,
  duelCodeBtn,
  duelHotseatBtn,
  grantNewSpecials,
  initFullscreen,
  shellEl,
  titleNameEl,
  poolNote,
  renderPoolNote,
  searchCancel,
  searchCpu,
  setSearchingText,
  showSearching,
  chooseTurnSeconds,
  chooseTargetWins,
  joinCode,
  langRow,
  linkClose,
  linkPanel,
  machineHasTurn,
  menuBack,
  menuBtn,
  menuPlay,
  menuResume,
  musicQuick,
  musicVol,
  copyShareLink,
  openPanel,
  panelOpen,
  pauseMenu,
  pauseHome,
  pauseRestart,
  pauseResume,
  pauseSetup,
  pauseShare,
  playerName,
  powerSlider,
  prepareSide,
  rankClose,
  rankPanel,
  rankRefresh,
  titleRanks,
  renderAwards,
  renderCharacters,
  renderLangs,
  renderOnlineRanks,
  renderRanks,
  refreshText,
  renderSetup,
  resultsAgain,
  resultsMenu,
  resultsRanks,
  resultsSetup,
  saveAim,
  screens,
  setRankStatus,
  setupBack,
  cpuSeg,
  chooseCpuLevel,
  setupGrid,
  setupP1,
  setupP2,
  setupStart,
  shareBtn,
  shareCode,
  shareMsg,
  showToast,
  hideToast,
  toastEl,
  attackBtn,
  guardBtn,
  braceBtn,
  renderLoadout,
  renderTurnChoices,
  renderTargetChoices,
  turnSeg,
  targetSeg,
  loadoutPanel,
  loadoutClose,
  renderSocial,
  socialNeedsTheShareSheet,
  socialRow,
  socialTarget,
  SOCIAL_URL,
  startBtn,
  updateJoinLabel,
  updateLabels,
  updateModeLabel,
  updateMusicLabel,
  updateSpecialButton,
  updateVolumeLabels,
} from './dom.js';
import { initAudio, playAward, setFxVolume, setMusic, setMusicVolume } from './audio.js';
import { announceLoot, brace, fireShot, newMatch, receiveShot, sendLook, showHandover, showInvite, showInviteCode, startTurnClock, syncOnline } from './flow.js';
import { startMatchCall } from './city.js';
import { onUnlock, adoptRivalLook, restoreLooks, savedName, setMode, setSavedName, introSeen, markIntroSeen, STARTER_SPECIALS } from './progress.js';
import { findRival, inPool, inRoom, leavePool, leaveRoom, openRoom, poolStatus } from './net.js';
import { POOL_TICK_MS, POOL_WAIT_MS, ROOM_SEAT_KEY } from './config.js';
import { LANGS, lang, setLang, t } from './i18n.js';
import { applyInvite, applyMatch, canonicalInvite, decodeInvite, decodeMatch, describeMatch, isInvite, newInvite } from './matchcode.js';
import { loadTop } from './leaderboard.js';
import { bootFrontEnd, inMatch, isPhone, pressStart, showScreen, skipSplash } from './screens.js';
import { draw } from './render/index.js';

const KEY_ANGLE_STEP = 1;
const KEY_POWER_STEP = 1;

/**
 * The seat this machine holds in a room, kept by code.
 *
 * The only thing about a room that is written down here. The code names the room and the token
 * is the way back into the side you had, so a reload lands on your own side rather than on
 * whoever is left standing — and nothing about the match is kept, because the match is the
 * match: whoever arrives is handed the board that is being played.
 */
function roomSeatToken(code) {
  try {
    const all = JSON.parse(localStorage.getItem(ROOM_SEAT_KEY) || '{}');
    return typeof all[code] === 'string' ? all[code] : '';
  } catch {
    return '';
  }
}

function rememberRoomSeat(code, token) {
  try {
    const all = JSON.parse(localStorage.getItem(ROOM_SEAT_KEY) || '{}');
    all[code] = token;

    // One entry per room anybody has ever been in is a list that only grows, and a seat is
    // worth keeping only while the room might still be up: ten of them is a long evening.
    const keys = Object.keys(all);
    while (keys.length > 10) delete all[keys.shift()];

    localStorage.setItem(ROOM_SEAT_KEY, JSON.stringify(all));
  } catch {
    // A browser that will not keep it means no seat to come back to, which is a smaller
    // disappointment than a game that refuses to start.
  }
}

/**
 * Archiva lo que hay en los mandos como la puntería de quien los tiene.
 *
 * Y se archiva en cada movimiento a mano, no sólo al disparar, que es lo que hacía: entre tu
 * tiro y el siguiente pasan segundos de sobra para ir dejando el ángulo y la fuerza del próximo,
 * y el turno cambia de dueño justo en medio. Al cambiar, `loadAim(prepareSide())` devolvía el
 * archivo viejo y se llevaba por delante lo que acababas de poner — el jugador lo cuenta como
 * «pongo el tiro del próximo turno y me lo deshace». Medido: disparas a 45/72, pones 60/90
 * mientras vuela el plátano, y a los 1.250 ms, al pasar el turno a la máquina, los mandos vuelven
 * solos a 45/72 y el tiro sale con ellos.
 *
 * `prepareSide()` es la pregunta que ya contesta de quién son los mandos aquí —la misma que usa
 * `loadAim` para devolverlos—, así que el archivo y la restauración no pueden discrepar. Y no se
 * dispara sola al escribir la máquina sus números: `angleSlider.value = …` no lanza `input`, sólo
 * lo hace una mano.
 */
function rememberAim() {
  saveAim(prepareSide());
}

/** Sets the angle slider and refreshes the preview. */
function applyAngle(value) {
  angleSlider.value = clamp(value, Number(angleSlider.min), Number(angleSlider.max));
  rememberAim();
  updateLabels();
  draw();
}

/** Sets the power slider and refreshes the preview. */
function applyPower(value) {
  powerSlider.value = clamp(value, Number(powerSlider.min), Number(powerSlider.max));
  rememberAim();
  updateLabels();
  draw();
}

/**
 * Asks the server for the shared board. The game never waits on this, and a
 * failure is something the panel explains rather than something that breaks.
 */
async function refreshOnline() {
  setRankStatus(t('rank.checking'));

  const res = await loadTop(10);

  // Todo traducido: esto eran tres frases en inglés a pelo —el único trozo del juego que no
  // pasaba por las tablas—, y pisaban la línea que sí está traducida.
  if (!res.ok) {
    const why = res.error === 'timeout' ? 'lb.timeout' : 'lb.noServer';
    // La lista vacía dice lo suyo (que no hay nadie en la tabla) y el motivo lo dice el estado de
    // arriba: antes los dos imprimían el mismo texto, y el jugador leía "no ranking server here" dos
    // veces seguidas, una como párrafo y otra como fila.
    renderOnlineRanks([], t('rank.emptyOnline'));
    setRankStatus(t(why));
    return;
  }

  renderOnlineRanks(res.entries);
  setRankStatus(res.entries.length ? t('lb.online') : t('rank.emptyOnline'));
}

function openRanks() {
  renderRanks();
  openPanel(rankPanel);
  // Opening the panel is the refresh gesture; nobody wants to press twice.
  refreshOnline();
}

/** The human's shot. Wrapped because it may just have spent a special. */
function playerFire() {
  // The key does what the button does. A disabled button cannot be clicked, so the mouse
  // can never arrive here in either of the two states below — but the keyboard is not a
  // mouse, and the space bar was throwing the machine's shot and the rival's, with a button
  // that said it could not.
  if (machineHasTurn() || fireBtn.disabled) return;

  initAudio();
  fireShot();
  updateSpecialButton();
}

/** Starts a match with whatever the setup screen has settled on. */
function startMatch() {
  // A gesture here means audio is allowed, so the round fanfare can play.
  initAudio();
  newMatch();

  // A match of this machine's own is nobody else's. Every one of these is read by the rest of the
  // game as "there is somebody on the other end": left up, a single player match started after a
  // duel would open the hand-over panel on its own turn and hand the turn to nobody. The two
  // paths that really are a duel put them back up immediately afterwards, which is why they are
  // cleared here rather than in newMatch(), which has never heard of them.
  state.online = false;
  state.byCode = false;
  state.awaitingRival = false;
  state.seat = 0;
  state.rival = '';

  updateModeLabel();
  updateLabels();
  updateSpecialButton();
  showScreen('game');
  draw();

  // Si esta es la primera partida del perfil lo dice quien llama, no esto: el panel de
  // equipamiento vive dentro de `wireInput` y esto está por encima. Se RESPONDE a la pregunta en
  // vez de marcar nada, de modo que una partida empezada por otra vía (una sala, un código) no
  // se coma el aviso: saldrá en la primera que sí lo pida.
  return !introSeen();
}

/**
 * Loads a match from a code, from either of the two places one can be typed.
 *
 * The panel has a field and a line under it for the complaint; the title screen has neither
 * and says it in a toast instead. Everything else about arriving in somebody else's match —
 * what has to be refreshed, in what order, and what announces it — is the same from both, so
 * it lives here rather than in the two listeners.
 *
 * `onError` is handed the message, which decodeMatch already has in the reader's language,
 * and decides where it goes. Nothing moves on a bad code: the player stays where he stands.
 */
/**
 * Takes an invitation: a fresh duel, with this machine as the one who was invited.
 *
 * The code says which city, which mode and which clock, and that is all there is to say — the
 * two of them have not started playing yet. It is applied over a match of this machine's own,
 * the way a long code is, because everything a match needs to exist has to be here first.
 */
function joinInvite(text, onError) {
  let invite;
  try {
    invite = decodeInvite(text);
  } catch (err) {
    onError(err.message);
    return false;
  }

  // La sala, con la forma con la que la generó el anfitrión: aquí entraba el texto tal cual se
  // tecleó, y `K7M2 QP4B` abría una sala distinta de `K7M2-QP4B` — dos jugadores solos.
  const code = canonicalInvite(text);

  startMatch();
  applyInvite(invite);
  setMode(invite.mode);
  chooseTurnSeconds(invite.seconds);
  // Y las rondas, si la invitación las trae: el ajuste local se pone al del anfitrión, igual que
  // con el reloj, para que la próxima partida de esta máquina juegue lo mismo.
  if (typeof invite.targetWins === 'number') chooseTargetWins(invite.targetWins);

  // La máquina se queda FUERA de esta partida, y es lo que no pasaba.
  //
  // `cpuOn` es un ajuste del setup —viene puesto por defecto, «1 jugador contra la CPU»— y quien se
  // une a un código desde la portada no pasa por el setup, así que llegaba a la partida con él en
  // true. Medido en una sala de dos jugadores de verdad: el invitado tenía `cpuOn: true` con
  // `online: true`, y eso rompe dos cosas a la vez. `advanceTurnClock` sale antes de cobrar el
  // turno (`if (state.cpuOn && state.turn === 1) return`), así que el reloj se quedaba clavado en
  // cero con el tiempo agotado hacía trece segundos y **el turno no expiraba nunca**; y
  // `maybeCpuTurn` habría disparado en nombre del jugador real en cuanto el turno llegara a él. El
  // comentario de `applyMatch` ya decía que sin esto «the machine plays the absent side on its own
  // turn»; la intención estaba escrita y no se cumplía.
  state.cpuOn = false;
  state.online = true;
  state.byCode = true;
  state.seat = 1;
  state.awaitingRival = false;

  updateLabels();
  updateModeLabel();
  renderSetup();
  closePanels();
  showScreen('game');
  showToast('🔗 ' + t('share.joined'));
  draw();

  // And into the room the code names, if the code has one: this is the same door the other end
  // opened, and from here on the turns travel by themselves. Seat one, because the code says
  // whose turn it is and that side is this one.
  openRoom({
    code,
    name: savedName(),
    seat: 1,
    token: roomSeatToken(code),
    onPeer: rivalArrived,
    onShot: receiveShot,
    onGone: rivalLeft,
    onClosed: roomClosed,
  }).then(res => {
    if (!res.ok || !inRoom()) return;
    state.byCode = false;
    state.seat = res.seat;
    state.room = code;
    // El otro asiento puede estar ya ocupado (quien abrió la sala antes), y su nombre viene con
    // la respuesta: sin esto la partida arrancaba contra "RIVAL" hasta el primer suceso `peer`.
    if (res.other && res.other.name) state.rival = String(res.other.name);
    updateLabels();
    rememberRoomSeat(code, res.token);
  });

  return true;
}

/**
 * The other seat is filled.
 *
 * Told rather than asked, and it happens twice in a room's life: the first time when whoever
 * the code was sent to turns up, and again whenever somebody comes back to a seat they left.
 * The reply is the same in both cases and it is the whole reason this is a function — hand the
 * board over. A room can be left and come back to, so the one arriving is not necessarily
 * sitting down to the match they left; the one who is here has it in front of them.
 */
function rivalArrived(peer = {}) {
  // El nome del rival viaja en el propio suceso — el servidor lo manda en `peer` — y aquí se
  // tiraba a la basura: el marcador se quedaba en "RIVAL" en las partidas por sala.
  if (peer && peer.name) state.rival = String(peer.name);

  // The seat is filled, so this is a match again: the other gorilla is drawn from here on, and
  // the way back into it is open again.
  const arrancando = state.callPending;
  state.awaitingRival = false;
  state.callPending = false;

  // And the clock of the turn that is about to be played starts now rather than when the board
  // was built: it has been standing still behind the panel, and a first turn half spent before
  // it began is a turn nobody had.
  startTurnClock();

  // A seat that had somebody in it and then did not is a match that was interrupted, and whoever
  // sits down in it starts it again: the turn goes back to the host, with the clock above to go
  // with it. The first arrival is the other case, and keeps the turn it was promised — the code
  // that went out said "your turn", and that is the turn the match opens with. Whether this is
  // the first arrival is exactly what the held-back start is: nothing has begun until one is.
  if (!arrancando) state.turn = 0;

  if (arrancando) startMatchCall();

  showToast('🔗 ' + t('share.roomBack'));
  updateLabels();
  syncOnline();
}

/**
 * The other seat is empty. Not the end of anything.
 *
 * The room stays open and the code stays good, so the match is not abandoned: it stops, the
 * seat is left free for whoever comes back to it — the same player or somebody else entirely —
 * and the one who is still here can wait, or go and change the settings, which is a new match
 * and theirs to start.
 */
function rivalLeft() {
  if (!inRoom()) return;
  // Nobody in the other seat, so there is nothing to unpause into: the match stops being one
  // that can be played, which is what takes the way back into it out of the pause. The room
  // stays open and the seat stays free — for them, or for whoever comes along with the code.
  state.awaitingRival = true;
  state.matchOver = false;
  closePanels();
  showToast('🚪 ' + t('share.roomGone'));
  showScreen('pause');
  updateLabels();
}

/**
 * The pipe has gone: no server, no room.
 *
 * Everything fails soft and in one direction here as well. The match carries on as a match of
 * this machine's own, which is what it becomes when there is nobody on the other end.
 */
function roomClosed() {
  leaveRoom();
  state.room = '';
  state.online = false;
  state.byCode = false;
  state.awaitingRival = false;
  showToast('📡 ' + t('share.roomLost'));
  updateLabels();
}

function loadFromCode(text, onError) {
  // A short code is not a match to carry on with, it is an invitation to one.
  if (isInvite(text)) return joinInvite(text, onError);

  let data;
  try {
    data = decodeMatch(text);
  } catch (err) {
    onError(err.message);
    return false;
  }

  // A code from a duel by correspondence names the side that wrote it, so the one who loads it
  // is the other one — and it says that the other side is a person, which is the one thing a
  // match cannot say about itself. Without it the machine plays the absent side on its own turn,
  // which is what the CPU is for and the last thing wanted here. Read before the match is
  // rebuilt, because the kits are handed out in there and a duel's second one is a person's.
  if (data.d === 0 || data.d === 1) {
    // Y la máquina no juega aquí: el lado ausente es una persona. Sin esto la CPU disparaba por él
    // cuando el turno llegaba a su asiento. Ver el comentario largo en joinInvite.
    state.cpuOn = false;
    state.online = true;
    state.byCode = true;
    state.seat = data.d ? 0 : 1;
    state.awaitingRival = false;
    adoptRivalLook(data.lk);
  }

  applyMatch(data);

  // applyMatch rebuilds the level, which resets the turn indicator, so that is put right
  // afterwards.
  updateLabels();
  updateModeLabel();
  updateSpecialButton();
  renderSetup();
  closePanels();
  showScreen('game');

  showToast('🔗 ' + describeMatch(data));
  draw();
  return true;
}

/**
 * The play button: into somebody else's match if there is a code in the field, and into the
 * menu if there is not.
 *
 * A code is a whole match — city, mode, round, whose turn it is and what has already been
 * blown up — so a player holding one has nothing left to answer. That is why JUGAR YA is the
 * thing that loads it rather than a button of its own: there is only ever one thing to press
 * on this screen.
 */
// Set while the pool is being asked, and whether the player asked for the CPU themselves —
// which decides what giving up means: the CPU, or back to the setup as it was.
let poolBusy = false;
let wantsCpu = false;

/**
 * Waits in the pool for a rival, with a way out on screen the whole time.
 *
 * The countdown is written from the same number the pool will give up on, so the promise on
 * the screen is the promise the code keeps. And nothing here can end in a button that does
 * nothing: nobody there, no server at all, or the player giving up early all hand them the
 * CPU instead — and say which of the three it was.
 */
async function startSearching() {
  if (poolBusy) return;
  poolBusy = true;

  // The click that got us here is the gesture audio is waiting for, and the match may well
  // begin without another one.
  initAudio();
  showSearching(true);

  const alone = await poolStatus();
  const until = Date.now() + POOL_WAIT_MS;

  const tick = () => {
    const left = t('search.seconds', { n: Math.max(0, Math.ceil((until - Date.now()) / 1000)) });
    const head = `${t('search.title')} · ${left}`;
    setSearchingText(alone && !alone.waiting ? `${head}\n${t('search.empty')}` : head);
  };

  tick();
  const ticker = setInterval(tick, POOL_TICK_MS);

  const answer = await findRival({
    // El nick guardado, que es el del jugador — no el campo del panel de clasificaciones, que
    // hasta abrir el panel está vacío y hacía que el pozo te presentara como PLAYER.
    name: savedName(),
    onShot: code => {
      showSearching(false);
      receiveShot(code);
    },
    onGone: () => {
      // They walked out mid-match. The match is not spoilt for it: the machine takes their
      // side over and the game carries on from where it stood.
      state.online = false;
      state.rival = '';
      restoreLooks();
      updateLabels();
    },
  });

  clearInterval(ticker);
  poolBusy = false;
  showSearching(false);

  if (answer.ok) {
    startOnlineMatch(answer);
    return;
  }

  if (answer.why === 'cancel' && !wantsCpu) {
    // Back to the setup as it was: giving up on the pool is not a reason to change somebody's
    // mind about the CPU for them.
    renderSetup();
    return;
  }

  wantsCpu = false;
  state.against = 'cpu';
  renderSetup();
  startMatch();
}

/**
 * The match the pool just found.
 *
 * Whoever was waiting is player one and brings the settings, exactly as they would if they
 * had sent a code. The flags go up before the match is built so that the first label drawn
 * already knows the other side is a person, and they are put up again afterwards: newMatch()
 * has never heard of them and could well have its own idea.
 */
function startOnlineMatch(answer) {
  state.online = true;
  state.seat = answer.seat;
  state.rival = answer.rival;
  state.cpuOn = false;

  if (answer.seat === 0) {
    startMatch();
    state.online = true;
    state.seat = answer.seat;
    state.rival = answer.rival;
    updateLabels();
    syncOnline();
    return;
  }

  // Whoever answered has no match of its own yet — the first code brings it — so who they are
  // goes out on its own, and then the panel says whose match is on its way.
  sendLook();
  showSearching(true);
  setSearchingText(t('search.matched', { name: answer.rival }));
}

function startOrJoin() {
  const code = joinCode ? joinCode.value.trim() : '';

  if (!code) {
    pressStart();
    return;
  }

  // The gesture browsers insist on before they will make a sound, code or no code: this
  // button is still the first thing pressed, and a match that began in silence would be a
  // bug. `stay` because the code is about to take us to the match ourselves.
  pressStart({ stay: true });

  loadFromCode(code, message => showToast('⚠️ ' + message));
}

/** True for anything the player could be typing into. */
function isTyping(target) {
  return target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement;
}

/**
 * Out of the setup screen. It is reachable from the menu and from the pause
 * screen, so BACK has to know which one it came from — and with a match live,
 * "back" always means back to the match, never to the menu it came from.
 */
function backFromSetup() {
  showScreen(state.matchLive ? 'pause' : 'menu');
}

/**
 * La única puerta de vuelta a la partida, y ninguna puede saltársela.
 *
 * Un duelo por correspondencia deja la partida montada y en el aire antes de que el rival esté
 * (`beginDuel`), y no hay nada a lo que tirar hasta que llegue: por eso RESUME de la pausa está
 * deshabilitado. Pero era la única puerta que lo sabía: Escape desde la pausa iba derecho al
 * tablero, y el VOLVER A LA PARTIDA del menú se guía por `matchLive`, que se pone en cuanto la
 * pantalla es la partida. Las dos pasan por aquí.
 */
function backToMatch() {
  if (state.awaitingRival) return;
  showScreen('game');
}

/**
 * Suelta el hilo, si lo hay, al salir de una partida para siempre.
 *
 * `pauseHome` decía "leave the match" y dejaba lo demás en pie: el flujo del pool abierto y
 * `state.online` en true, así que el rival llegando — o tirando — llamaba a `showScreen('game')` y
 * devolvía a esta máquina a la partida que acababa de dejar. Las dos llamadas son seguras cuando no
 * hay nada que dejar.
 */
function leaveTheWire() {
  // También se suelta cuando queda un hilo abierto sin `online`: un camino que empezaba partida
  // local bajaba la bandera y dejaba la sala viva, con el rival tirando hacia el vacío.
  if (!state.online && !inRoom() && !inPool()) return;
  state.online = false;
  state.room = '';
  state.rival = '';
  leaveRoom();
  leavePool();
}

/** Escape, from wherever the player happens to be standing. */
function goBack() {
  if (panelOpen()) {
    closePanels();
    return;
  }

  // Escape in a match opens the pause rather than doing nothing, which is what it
  // did: it read as a key that was not wired up. Pressed again it closes it, so the
  // one key is the whole of the match's menu.
  if (state.screen === 'game') showScreen('pause');
  else if (state.screen === 'setup') backFromSetup();
  else if (state.screen === 'menu') showScreen(state.matchLive ? 'pause' : 'title');
  else if (state.screen === 'pause') backToMatch();
  else if (state.screen === 'results') showScreen('menu');
}

export function wireInput() {
  // El regalo, con el ratón o con el dedo: el más rápido se lo lleva, y contra la máquina eso
  // incluye a la máquina — su margen y su anillo viven en crates.js.
  //
  // No cuesta el turno y no dispara nada: es lo único del campo que responde a un toque, y por eso
  // el área de acierto puede ser generosa (ver CRATE_GRAB_R) sin pisar nada más.
  gameCanvas.addEventListener('pointerdown', event => {
    if (state.screen !== 'game') return;

    const r = gameCanvas.getBoundingClientRect();
    const loot = grabCrate(
      (event.clientX - r.left) * (W / r.width),
      (event.clientY - r.top) * (H / r.height),
      humanSide(),
    );

    if (loot) announceLoot(loot);
  });

  // Y el cartel se va con un toque: un logro o una recompensa se quedan encima del campo
  // mientras el jugador apunta, y esperar a que se apaguen solos es una espera que no toca.
  // El cartel se traga el toque (no lleva `pointer-events: none`), así que el gesto se lee
  // aquí y no llega al campo: cerrar el aviso no dispara nada de lo que hay debajo.
  toastEl.addEventListener('pointerdown', () => hideToast());

  // --- the front-end -------------------------------------------------------

  // Any click on the splash skips it, for anyone quicker than 1.7 seconds.
  screens.splash.addEventListener('click', skipSplash);

  startBtn.addEventListener('click', () => startOrJoin());

  // Enter in the code field is the same button. The global handler below steps aside for
  // anything being typed into — that is what keeps the arrow keys out of the sliders while
  // somebody types — so the field has to say for itself what Enter means.
  // And the button above it changes what it says as soon as there is a code in there.
  joinCode.addEventListener('input', updateJoinLabel);

  joinCode.addEventListener('keydown', event => {
    if (event.key !== 'Enter') return;
    event.preventDefault();
    startOrJoin();
  });

  menuResume.addEventListener('click', () => backToMatch());

  // The way out to the front page, which is where the code field is. Until now the only route to
  // the title was Escape, and Escape on the menu goes to the pause while a match is still running
  // - so somebody handed a code with a match on the go had no way to reach the field that takes
  // it. It does not touch the match: the menu goes on offering BACK TO MATCH, the same as the
  // pause's own way out to the front page does.
  if (menuBack) menuBack.addEventListener('click', () => showScreen('title'));

  menuPlay.addEventListener('click', () => {
    renderSetup();
    showScreen('setup');
  });

  setupGrid.addEventListener('click', event => {
    const card = event.target.closest('.modecard');
    if (!card) return;

    // Choosing rules does not start anything: that is what START MATCH is for. Y tampoco se
    // aplican a la partida que ya está en curso: se guardan y `newMatch` las pone cuando la
    // siguiente empieza. Aplicarlas aquí cambiaba los cráteres y la gravedad a mitad de partida
    // mientras el reloj y las rondas se quedaban como estaban.
    setMode(card.dataset.id);
    renderSetup();
    draw();
  });

  [[setupP1, true], [setupP2, false]].forEach(([el, cpu]) => {
    el.addEventListener('click', () => {
      state.cpuOn = cpu;
      renderSetup();
      updateModeLabel();
      draw();
    });
  });

  // Where the second player is, under two players. Chosen here and read by beginMatch.
  [[duelCodeBtn, 'code'], [duelHotseatBtn, 'hotseat']].forEach(([el, duel]) => {
    if (!el) return;
    el.addEventListener('click', () => {
      state.duel = duel;
      renderSetup();
    });
  });

  // Restarting a duel by correspondence keeps it a duel: newMatch() has never heard of these
  // flags, and a code written after a restart without them would reach the other machine as an
  // ordinary single-machine match — with the machine playing the side that is not there.
  const restartMatch = () => {
    const duel = state.byCode;
    const seat = state.seat;
    const rivalName = state.rival;
    // Una revancha es la misma partida, así que el hilo sigue: en una sala o en el pozo esta
    // función solo miraba `byCode`, que allí es false, y dejaba `online` en false mientras el
    // stream seguía abierto — se dejaba de mandar y de aplicar al rival sin decir nada, que es
    // "no se conectan y no se ve nada del otro".
    const wired = state.online && (inRoom() || inPool());
    startMatch();
    if (duel || wired) {
      // Y sigue sin jugar la máquina: es la misma partida de antes. Ver joinInvite.
      state.cpuOn = false;
      state.online = true;
      state.byCode = duel;
      state.seat = seat;
      state.rival = rivalName;
      updateLabels();
      syncOnline();
    }
  };

  cpuSeg.addEventListener('click', event => {
    const el = event.target.closest('[data-level]');
    if (el) chooseCpuLevel(el.dataset.level);
  });

  // The turn time, chosen the same way the players are. Off is one of the choices
  // rather than a separate switch: a clock is a match rule, and a table of two
  // people sharing a keyboard may not want one at all.
  turnSeg.addEventListener('click', event => {
    const chosen = event.target.closest('.seg');
    if (!chosen || chosen.dataset.seconds === undefined) return;

    chooseTurnSeconds(Number(chosen.dataset.seconds));
    renderTurnChoices();
    updateLabels();
  });

  // And how long the match is: the same row of buttons, built from the same kind of list. What it
  // chooses is what the next match is built with — see chooseTargetWins, which is why nothing here
  // touches the match that is already on.
  targetSeg.addEventListener('click', event => {
    const chosen = event.target.closest('.seg');
    if (!chosen || chosen.dataset.wins === undefined) return;

    chooseTargetWins(Number(chosen.dataset.wins));
  });

  // --- the phone, held upright -----------------------------------------------
  //
  // A phone held up cannot play this, and the front end will happily let it try: the board comes
  // out 336x190 on a 390x844 screen, a fifth of the glass, against 660x378 the moment the phone
  // is on its side. So the match waits for the phone to be turned.
  //
  // There is no way to make a phone rotate. `screen.orientation.lock()` works only in
  // fullscreen, an iOS Safari has no `requestFullscreen` for an element at all and Chrome's lock
  // is for mobile only, so the mechanism is the notice and the lock is the fast path: on an
  // Android the phone turns itself and the notice is never seen.
  //
  // The question itself — held upright or not — lives in screens.js next to `matchOnHold`, because
  // the notice, the turn clock and the loop all ask it and only one of them is about input.

  /**
   * Asks the platform to turn the phone. Resolves true only when it actually turned it.
   *
   * Every rejection in here is an expected one and is swallowed on purpose: an iPhone has no
   * lock to call and a desktop has one that refuses. Only the caller cares which happened.
   */
  function askForLandscape() {
    if (!shellEl.requestFullscreen || !screen.orientation || typeof screen.orientation.lock !== 'function') {
      return Promise.resolve(false);
    }

    return shellEl.requestFullscreen()
      .then(() => screen.orientation.lock('landscape'))
      .then(() => true)
      .catch(() => false);
  }

  function beginDuel() {
    // The match is built and then handed straight to the other side: the code that goes out is
    // the one that says "your turn", which is the only code worth sending. So the turn is put
    // on the side that is not here, and there is nothing for this one to shoot at until the
    // reply comes back — and that side is not drawn either, because nobody has chosen it yet:
    // see awaitingRival.
    //
    // The flag goes up before the board is built, because building it is what calls the round and
    // plays the fanfare (see startMatchCall), and there is nobody to call a round to yet. Then it
    // goes up again on the way out, because startMatch() clears it: that function has no way of
    // knowing which kind of match it has just built.
    state.awaitingRival = true;
    startMatch();
    // Aquí el anfitrión ya viene del setup con `cpuOn` apagado (eligió «2 jugadores»), pero se
    // escribe igual: esta función no tiene forma de saber por dónde se ha llegado hasta ella.
    state.cpuOn = false;
    state.online = true;
    state.byCode = true;
    state.seat = 0;
    state.awaitingRival = true;
    state.turn = 1;
    startTurnClock();
    updateLabels();

    // The invitation names the city, so it is made first and the match is built from it. A room
    // that is already open keeps its code: it is the same name, so restarting the match — the
    // settings changed, the board rebuilt — is not a new thing to send anybody.
    const code = state.room || newInvite();
    showInvite(code);

    // And the room, when there is a server to hold it. Then the code is a door rather than a
    // turn to be carried: the same code opens it all evening, and the turns go by themselves.
    // Without a server everything stays as it was — the code is carried by hand, one per turn,
    // which is the floor this falls back to and not the ceiling it aims at.
    openRoom({
      code,
      name: savedName(),
      seat: 0,
      token: roomSeatToken(code),
      onPeer: rivalArrived,
      onShot: receiveShot,
      onGone: rivalLeft,
      onClosed: roomClosed,
    }).then(res => {
      if (!res.ok) return;
      state.byCode = false;
      state.seat = res.seat;
      state.room = code;
      // Quien ya estaba dentro del otro asiento: su nombre es el del rival que se va a tener
      // enfrente, y viene en `hello.other` cuando la sala ya existía.
      if (res.other && res.other.name) state.rival = String(res.other.name);
      updateLabels();
      rememberRoomSeat(code, res.token);
      // The line under the code says what the code is now: a room that is open, not a turn
      // waiting to be delivered.
      shareMsg.textContent = t('share.roomOpen');
    });
  }

  function beginMatch() {
    // Two players, each on their own phone: the game's job is to get the code out and out of
    // the way. Zero players in the room would be a different game and one in the room is the
    // pool's, which is the other branch below.
    if (!state.cpuOn && state.against !== 'random' && state.duel === 'code') {
      beginDuel();
      return;
    }
    // Cualquier otra cosa que se empiece desde aquí es una partida local, y una partida local no
    // tiene a nadie al otro lado: el hilo de la que hubiera se suelta, o se queda una sala viva
    // devolviendo a esta máquina a la partida anterior.
    leaveTheWire();
    // The one question the setup asks about a single player is who they are up against, and
    // it is asked here rather than on the title screen: the mode, the turn time and the
    // difficulty are all part of the same answer, and a random rival is the other answer.
    if (state.cpuOn && state.against === 'random') startSearching();
    else if (startMatch()) firstMatchIntro();
  }

  setupStart.addEventListener('click', () => {
    // La partida empieza siempre, y el aviso de girar es un aviso y no una puerta. Cuando el
    // teléfono está de pie lo pinta la propia orientación en cuanto el tablero es la pantalla
    // (`[data-screen='game']`), y el turno no corre mientras el aviso está delante
    // (`matchOnHold`), así que girar es seguir jugando — sin tener que volver a pulsar nada.
    // Antes esto era una puerta: sin girar no se construía la partida, y en un iPhone (que no
    // sabe girarse solo) el jugador se quedaba en los ajustes con el aviso encima.
    //
    // La petición al sistema se deja hecha, que es el atajo de los Android: si el teléfono gira
    // solo, el aviso no se llega a ver. Pero **sólo en un teléfono**: en un ordenador no hay
    // orientación que bloquear, así que la petición no compraba nada y en cambio ponía la ventana a
    // pantalla completa sin que nadie se lo hubiera pedido — medido, al pulsar EMPEZAR PARTIDA el
    // `shell` entraba en pantalla completa y el tablero se reajustaba a 931 px en una ventana de
    // 1176. Entrar a pantalla completa cuando uno quiere sigue estando en el doble clic del campo.
    if (isPhone()) askForLandscape();
    beginMatch();
  });
  setupBack.addEventListener('click', backFromSetup);

  // Who the single player is up against. The difficulty row below is the CPU's follow-up,
  // and dom.js draws the two of them together, so this only has to set the choice.
  if (againstCpuEl) {
    againstCpuEl.addEventListener('click', () => {
      state.against = 'cpu';
      renderSetup();
    });
  }

  if (againstRandomEl) {
    againstRandomEl.addEventListener('click', async () => {
      state.against = 'random';
      renderSetup();
      // How many are waiting, so the button can say it before it is pressed instead of
      // promising something it cannot deliver.
      renderPoolNote(await poolStatus());
    });
  }

  if (searchCancel) searchCancel.addEventListener('click', () => leavePool());
  if (searchCpu) {
    searchCpu.addEventListener('click', () => {
      // Giving up on the pool is a decision rather than a failure: the match happens either
      // way, it is only the other side that changes.
      wantsCpu = true;
      leavePool();
    });
  }

  // --- pause and results ---------------------------------------------------

  menuBtn.addEventListener('click', () => {
    if (state.screen === 'game') showScreen('pause');
  });

  // Fullscreen, next to the pause button because it is the other thing that floats over
  // the field without being a control of the shot.
  initFullscreen();

  pauseResume.addEventListener('click', () => backToMatch());
  pauseRestart.addEventListener('click', restartMatch);
  pauseSetup.addEventListener('click', () => {
    renderSetup();
    showScreen('setup');
  });
  pauseMenu.addEventListener('click', () => showScreen('menu'));
  if (pauseHome) {
    pauseHome.addEventListener('click', () => {
      // Out of the match for good: the flag the front end hangs off comes down, so the menu
      // offers a new match rather than the one just left, and the screen goes to the title —
      // which is the front page, code field and all. The other button is the temporary one.
      //
      // Y el hilo se suelta: sin esto, el rival que llegue o que tire devuelve a esta máquina a
      // una partida que acaba de dejar (`receiveShot` termina en `showScreen('game')`).
      leaveTheWire();
      state.matchLive = false;
      showScreen('title');
    });
  }

  resultsAgain.addEventListener('click', restartMatch);
  resultsSetup.addEventListener('click', () => {
    renderSetup();
    showScreen('setup');
  });
  resultsRanks.addEventListener('click', openRanks);
  resultsMenu.addEventListener('click', () => showScreen('menu'));

  // --- the match -----------------------------------------------------------

  fireBtn.addEventListener('click', () => playerFire());
  // Bracing: the other way to spend a turn, and the only way to spend a defence now
  // that an attack and a defence can be held at the same time.
  braceBtn.addEventListener('click', () => brace());
  // Two doors, one each, into the same chooser. What the player carries is still a
  // single choice — a shot spends one thing and a defence is a turn of its own — so
  // the buttons pick which half of the bag to look in, not two separate loadouts.
  attackBtn.addEventListener('click', () => openLoadout('ult'));
  guardBtn.addEventListener('click', () => openLoadout('guard'));

  // Sharing, from the title screen. Two of these do not point anywhere: Instagram has
  // no web address that takes a link, so it asks for the phone's share sheet, and the
  // last one copies instead. Everything else is an ordinary link opened in a new tab,
  // with noopener so the game's own window cannot be reached from the other end.
  socialRow.addEventListener('click', event => {
    const button = event.target.closest('button[data-social]');
    if (!button) return;

    const id = button.dataset.social;

    if (socialNeedsTheShareSheet(id) && navigator.share) {
      navigator.share({ url: SOCIAL_URL }).catch(() => {});
      return;
    }

    const target = socialTarget(id);
    if (target) window.open(target, '_blank', 'noopener,noreferrer');
    else copyShareLink();
  });

  // The equipment menu, which is the Worms move: reach into the bag in the middle
  // of a match without leaving it. Two buttons for the mouse, in the bar, and a key
  // for the keyboard, and all three of them are the MATCH's. The pause menu used to
  // carry a fourth way in and no longer does: choosing what to throw belongs to a
  // turn, not to the screen you are looking at between turns.
  // Which half the open panel is showing, so picking from it does not suddenly
  // reveal the other half underneath the player's hands.
  let loadoutKind = null;

  /**
   * La primera partida dice dónde vive el equipamiento: un cartel lo anuncia con el botón que hay
   * que pulsar. Es la mitad del asunto — las habilidades están ahí desde el primer tiro, pero el
   * único camino a ellas era un botón que sólo promete «ATAQUE» — y se dice una sola vez en la vida
   * del perfil, porque un aviso que sale en todas deja de ser un aviso y pasa a ser ruido tapando
   * el campo justo cuando el jugador está apuntando. El resto del cómo se usa cada mitad está en la
   * nota del propio panel (`loadout.note`).
   *
   * El panel NO se abre solo: lo abrió una versión anterior de esto y el jugador se encontraba una
   * pantalla encima de la partida recién empezada. El aviso dice qué botón lo abre, que es lo que
   * hace falta saber; abrirlo por él es quitarle el turno para leer.
   */
  const firstMatchIntro = () => {
    markIntroSeen();
    showToast(t('intro.welcome', { n: STARTER_SPECIALS.length }), { kind: 'reward' });
  };

  const openLoadout = (kind = null) => {
    loadoutKind = kind;
    renderLoadout(kind);
    openPanel(loadoutPanel);
  };

  loadoutClose.addEventListener('click', () => closePanels());

  loadoutPanel.addEventListener('click', event => {
    const card = event.target.closest('.loadoutcard');
    if (!card || card.disabled) return;

    // Picking the one already armed puts it away again, so the menu can be used
    // to change your mind as well as to decide. Which of the two slots it goes in is
    // the card's own kind: an attack and a defence are held at once now, and choosing
    // in one half no longer throws the other away.
    // The slot that gets written is the one the panel is showing, which is not the same
    // thing as the turn being played: while the other side is in the air this is this
    // side's next turn being set up. See prepareSide().
    const side = prepareSide();
    const slot = card.dataset.kind === 'guard' ? state.guard : state.special;
    slot[side] = slot[side] === card.dataset.id ? null : card.dataset.id;
    renderLoadout(loadoutKind);
    updateSpecialButton();
    updateLabels();

    // And then it shuts itself. Choosing is the last thing the menu was open for,
    // so leaving it up meant closing it by hand every single turn before the throw
    // could be aimed. The click that lands on a card is the click that ends the
    // decision; Escape and the close button are still there for anyone who opened
    // it just to read.
    closePanels();
  });

  window.addEventListener('keydown', event => {
    if (event.key !== 'i' && event.key !== 'I') return;
    // The match itself, not the pause: see the note over the equipment menu above.
    if (isTyping(event.target) || state.screen !== 'game') return;
    if (event.metaKey || event.ctrlKey || event.altKey) return;
    // Open on anybody's turn now. It is not a door into the machine's bag — the panel
    // speaks for this side whether or not it is the one throwing, see prepareSide() — and
    // the other side's turn is exactly when there is time to plan the next one.

    if (panelOpen()) closePanels();
    else openLoadout();
  });

  angleSlider.addEventListener('input', () => {
    rememberAim();
    updateLabels();
    draw();
  });

  powerSlider.addEventListener('input', () => {
    rememberAim();
    updateLabels();
    draw();
  });

  // The one way the music is ever asked for, so the two of them cannot drift apart: the
  // speaker in the header, and the level in the pause menu when it leaves zero.
  const wantMusic = on => {
    initAudio();
    if (state.audioCtx.state === 'suspended') state.audioCtx.resume();
    setMusic(on);
    updateMusicLabel();
  };

  musicQuick.addEventListener('click', () => wantMusic(!state.musicOn));

  // The two levels. `input` rather than `change`, so the sound follows the thumb while it is
  // being dragged — which is the only way to set a level by ear.
  [[musicVol, setMusicVolume], [fxVol, setFxVolume]].forEach(([el, apply]) => {
    el.addEventListener('input', () => {
      apply(Number(el.value) / 100);
      updateVolumeLabels();
    });
  });

  // The music level is the music's switch as well, now that the panel has a single control for
  // it: a level that leaves zero is a request to hear it, and a level that lands there is a
  // request to stop. Only on the change of state, so dragging the thumb does not re-ask for the
  // music on every pixel, and the level itself is untouched — coming back up comes back to the
  // same number it was at.
  musicVol.addEventListener('input', () => {
    const on = Number(musicVol.value) > 0;
    if (on !== state.musicOn) wantMusic(on);
  });

  awardsBtn.addEventListener('click', () => {
    // One panel at a time, so opening awards closes whatever was already up.
    if (awardPanel.hidden) openPanel(awardPanel);
    else closePanels();
  });

  awardClose.addEventListener('click', closePanels);

  // --- language -------------------------------------------------------------
  // On the title screen, because a language picker you can only reach after
  // reading the menu in a language you do not have is not a picker at all.
  langRow.addEventListener('click', event => {
    const pick = event.target.closest('.langbtn');
    if (!pick || pick.dataset.lang === lang()) return;

    setLang(pick.dataset.lang);
    // Y la URL dice lo mismo, porque cada idioma es una página de verdad: `/` para el inglés y
    // `/es/`, `/it/`… para el resto (las genera tools/prerender-langs.mjs, con su canonical y
    // sus hreflang). Así el enlace que alguien copie lleva su idioma. Sin recargar -se perdería
    // la partida- y con replaceState, que no ensucia el historial de atrás.
    try {
      history.replaceState(null, '', pick.dataset.lang === 'en' ? '/' : `/${pick.dataset.lang}/`);
    } catch {
      // Con file:// o desde un servidor que no deja, la página sigue funcionando igual.
    }

    refreshText();
    draw();
  });

  // --- characters and leaderboards ----------------------------------------

  charsBtn.addEventListener('click', () => {
    renderCharacters();
    openPanel(charPanel);
  });

  // El panel de personajes tiene un solo botón y cierra desde el fondo, que es donde está el dedo
  // cuando se termina de elegir: ver `#charpanel .charsave` en styles.css. El ✕ de la cabecera se
  // quitó a petición, porque eran dos botones haciendo el mismo trabajo.
  charSave.addEventListener('click', closePanels);

  charList.addEventListener('click', event => {
    const pick = event.target.closest('.species, .acc');
    if (!pick || !applyCharacterClick(pick)) return;

    // Redrawn from scratch every click: one species is chosen at a time, and an
    // accessory toggles, so the panel is the cheapest place to keep it honest.
    renderCharacters();
    draw();
  });

  // --- leaderboards, and the panel that also lists them -------------------

  rankClose.addEventListener('click', closePanels);

  titleRanks.addEventListener('click', () => openRanks());
  rankRefresh.addEventListener('click', () => refreshOnline());

  // Saved on every keystroke: there is no save button, and an empty name simply
  // means "keep my matches on this machine".
  //
  // Y el campo es uno solo —el de la portada, junto al código de partida— desde que el jugador pidió
  // quitar el del panel de clasificaciones. Cuando eran dos se escribían juntos, porque eran un
  // nombre con dos puertas; con una sola puerta queda el `forEach` de abajo, que sigue valiendo para
  // una lista de uno.
  const nameFields = [titleNameEl].filter(Boolean);
  nameFields.forEach((field) => {
    field.addEventListener('input', () => {
      setSavedName(field.value);
      nameFields.forEach((other) => {
        if (other !== field) other.value = field.value;
      });
    });
  });
  if (titleNameEl) titleNameEl.value = savedName();

  // --- share and load a match ---------------------------------------------

  if (pauseShare) pauseShare.addEventListener('click', openShare);

  /**
   * Opens the panel that carries the code, from the menu and from the pause.
   *
   * The box keeps an invitation. It is the code that gets somebody into this match, it is written
   * into the box once, and it cannot be made again by asking for it a second time: the seed, the
   * mode, the clock and the look are settled in it. So what is in the box is read rather than the
   * state of the duel: a four-four code in there is an invitation whatever the match is doing, and
   * it stays where it is — closing the panel and opening it again used to throw it away, because
   * the box was emptied whenever nothing was waiting for a rival.
   *
   * A long code in the box is a turn of a correspondence match, which is written again each time
   * it goes, and there the button to make an invitation is offered: the duel under way is asking
   * for a different thing.
   */
  function openShare() {
    if (isInvite(shareCode.value)) {
      if (shareBtn) shareBtn.hidden = true;
    } else {
      if (shareBtn) shareBtn.hidden = false;
      shareCode.value = '';
      shareMsg.textContent = '';
    }

    openPanel(linkPanel);
  }

  linkClose.addEventListener('click', closePanels);

  shareBtn.addEventListener('click', () => {
    // The code this hands over is the invitation, four and four: the short one, the one that is
    // read out to the other player or pasted on their front page. The long codes exist too, one
    // per turn of a match in progress, but they are not this button's business — they belong to
    // a duel already under way and they put themselves in this box when the turn leaves.
    const code = newInvite();
    showInviteCode(code);

    // The clipboard API needs permission and a secure context, and this game is happy to run off
    // a file:// path. So it is offered, never depended on — and a refusal needs no words of its
    // own: the code is on screen and selected by the line below, and the line above it is already
    // telling the player what to do with it. Saying "the browser refused" was telling them about
    // a browser they cannot see, in place of the only thing they have to do either way.
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(code).then(
        () => {
          shareMsg.textContent = t('share.copied');
          // Copied, so the highlight has done its job. It is left where it is on a refusal,
          // which is the case it is there for: one keystroke and the code is on its way.
          shareCode.setSelectionRange(0, 0);
        },
        () => {},
      );
    }

    shareCode.select();
  });

  // One event can earn several achievements at once, and they all arrive in the
  // same tick, so they are collected and announced together rather than each
  // one overwriting the last.
  let pendingUnlocks = [];
  let unlockTimer = null;

  onUnlock(def => {
    pendingUnlocks.push(def.name);
    clearTimeout(unlockTimer);
    unlockTimer = setTimeout(() => {
      // The loudest thing the game says, and it says it once for the lot: several can land
      // in the same tick, and the player reading one name at a time misses the first two.
      //
      // Dos nombres como mucho y el resto contado. Con cinco de golpe el cartel se estiraba
      // hasta pegarse a los bordes en un móvil de pie (medido, 391 px de ancho sobre 390 de
      // pantalla, y 122 de alto): el cartel es una pastilla, no un párrafo. El "+2" va sin
      // traducir porque se lee igual en los seis idiomas.
      const names = pendingUnlocks.length > 2
        ? pendingUnlocks.slice(0, 2).join(' · ') + ' +' + (pendingUnlocks.length - 2)
        : pendingUnlocks.join(' · ');
      showToast(t('toast.award', { names }), { kind: 'award' });
      playAward();
      pendingUnlocks = [];
    }, 0);

    grantNewSpecials();
    renderAwards();
    updateSpecialButton();
  });

  // --- keyboard ------------------------------------------------------------

  window.addEventListener('keydown', event => {
    // Never steal a keystroke from someone typing a name or pasting a code.
    if (isTyping(event.target)) return;

    if (!inMatch()) {
      // Any key gets past the splash. After that Enter is the way forward and
      // Escape is the way back, so the front-end works without the mouse.
      if (state.screen === 'splash') {
        skipSplash();
      } else if (event.key === 'Escape') {
        goBack();
      } else if (event.key === 'Enter') {
        if (state.screen === 'title') startOrJoin();
        else if (state.screen === 'menu') menuPlay.click();
        // Las dos preguntas del setup, no una: `startMatch` a pelo se saltaba el duelo por
        // correspondencia y la cola de rival — con dos jugadores cada uno en su móvil, Enter
        // arrancaba una partida local en silencio —, y en los resultados perdía las banderas del
        // duelo que el propio REVANCHA conserva. Ahora son el mismo camino que sus botones.
        else if (state.screen === 'setup') beginMatch();
        else if (state.screen === 'results') restartMatch();
        else return;
      } else {
        return;
      }

      event.preventDefault();
      return;
    }

    if (event.key === 'Escape') {
      // Las dos ramas separadas, y la de volver pasa por la puerta: con el rival aún sin llegar, un
      // Escape dejaba al jugador dentro de una partida que no puede jugar, que es justo lo que el
      // botón RESUME deshabilitado está diciendo que no se hace.
      if (state.screen === 'pause') backToMatch();
      else showScreen('pause');

      event.preventDefault();
      return;
    }

    // A paused match is not taking shots.
    if (state.screen !== 'game') return;

    switch (event.key) {
      case 'ArrowUp':
        applyAngle(Number(angleSlider.value) + KEY_ANGLE_STEP);
        break;
      case 'ArrowDown':
        applyAngle(Number(angleSlider.value) - KEY_ANGLE_STEP);
        break;
      case 'ArrowRight':
        applyPower(Number(powerSlider.value) + KEY_POWER_STEP);
        break;
      case 'ArrowLeft':
        applyPower(Number(powerSlider.value) - KEY_POWER_STEP);
        break;
      case ' ':
      case 'Enter':
        playerFire();
        break;
      default:
        return;
    }

    event.preventDefault();
  });

  // --- first paint ---------------------------------------------------------

  renderAwards();
  renderSetup();
  renderCharacters();
  renderLangs();
  renderSocial();
  renderTurnChoices();
  renderTargetChoices();
  updateSpecialButton();
  updateMusicLabel();
  bootFrontEnd();
}
